/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 * 
 * Trial signup page for Denver - shows fake lead data to prospective agents
 * Easily duplicable for other cities by changing CITY_CONFIG
 */
import React, { useState, useEffect } from 'react';
import { Lock, X } from 'react-feather';

// ============ CITY CONFIGURATION ============
// Duplicate this file and change these values for other cities
const CITY_CONFIG = {
  cityName: 'Denver',
  state: 'CO',
  trialUrl: '/trial-start',
  neighborhoods: [
    'Denver, CO',
    'Aurora, CO', 
    'Lakewood, CO',
    'Arvada, CO',
    'Westminster, CO',
    'Thornton, CO',
    'Broomfield, CO',
    'Centennial, CO'
  ]
};

// ============ FAKE LEAD DATA ============
// Realistic-looking leads with good metrics to entice agents
const FAKE_LEADS = [
  {
    id: 1,
    name: 'Sarah M.',
    tier: 'platinum',
    location: 'Denver, CO',
    creditScore: 762,
    monthlyIncome: 8200,
    swipes: 47,
    onboarding: {
      budget: { min: 450000, max: 650000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '1-3 months',
      amenities: ['hardwood', 'garage', 'garden'],
      preApproved: true
    }
  },
  {
    id: 2,
    name: 'Marcus T.',
    tier: 'gold',
    location: 'Denver, CO',
    creditScore: 718,
    monthlyIncome: 6800,
    swipes: 34,
    onboarding: {
      budget: { min: 350000, max: 500000 },
      bedrooms: 2,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['aircon', 'balcony', 'gym'],
      preApproved: true
    }
  },
  {
    id: 3,
    name: 'Jennifer L.',
    tier: 'platinum',
    location: 'Lakewood, CO',
    creditScore: 789,
    monthlyIncome: 9400,
    swipes: 52,
    onboarding: {
      budget: { min: 550000, max: 800000 },
      bedrooms: 4,
      bathrooms: 3,
      livingSituation: 'ownhome',
      timeline: '1-3 months',
      amenities: ['pool', 'view', 'walkincloset', 'office'],
      preApproved: true
    }
  },
  {
    id: 4,
    name: 'David K.',
    tier: 'gold',
    location: 'Denver, CO',
    creditScore: 695,
    monthlyIncome: 5600,
    swipes: 28,
    onboarding: {
      budget: { min: 300000, max: 450000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'family',
      timeline: '3-6 months',
      amenities: ['garage', 'garden', 'fireplace'],
      preApproved: false
    }
  },
  {
    id: 5,
    name: 'Amanda R.',
    tier: 'silver',
    location: 'Westminster, CO',
    creditScore: 708,
    monthlyIncome: 3600,
    swipes: 19,
    onboarding: {
      budget: { min: 250000, max: 350000 },
      bedrooms: 2,
      bathrooms: 1,
      livingSituation: 'renting',
      timeline: '6+ months',
      amenities: ['aircon', 'balcony'],
      preApproved: false
    }
  },
  {
    id: 6,
    name: 'Michael B.',
    tier: 'gold',
    location: 'Denver, CO',
    creditScore: 734,
    monthlyIncome: 7200,
    swipes: 41,
    onboarding: {
      budget: { min: 400000, max: 550000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '1-3 months',
      amenities: ['hardwood', 'office', 'security'],
      preApproved: true
    }
  },
  {
    id: 7,
    name: 'Rachel S.',
    tier: 'platinum',
    location: 'Broomfield, CO',
    creditScore: 756,
    monthlyIncome: 8800,
    swipes: 63,
    onboarding: {
      budget: { min: 500000, max: 700000 },
      bedrooms: 4,
      bathrooms: 3,
      livingSituation: 'ownhome',
      timeline: '1-3 months',
      amenities: ['pool', 'garden', 'walkincloset', 'garage'],
      preApproved: true
    }
  },
  {
    id: 8,
    name: 'Chris W.',
    tier: 'bronze',
    location: 'Commerce City, CO',
    creditScore: 542,
    monthlyIncome: 3200,
    swipes: 12,
    onboarding: {
      budget: { min: 150000, max: 250000 },
      bedrooms: 2,
      bathrooms: 1,
      livingSituation: 'family',
      timeline: '6+ months',
      amenities: ['aircon'],
      preApproved: false
    }
  },
  {
    id: 9,
    name: 'Emily H.',
    tier: 'platinum',
    location: 'Denver, CO',
    creditScore: 771,
    monthlyIncome: 8500,
    swipes: 38,
    onboarding: {
      budget: { min: 475000, max: 675000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '1-3 months',
      amenities: ['view', 'balcony', 'hardwood', 'gym'],
      preApproved: true
    }
  },
  {
    id: 10,
    name: 'Jason P.',
    tier: 'gold',
    location: 'Littleton, CO',
    creditScore: 702,
    monthlyIncome: 5900,
    swipes: 31,
    onboarding: {
      budget: { min: 325000, max: 475000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['garage', 'fireplace', 'garden'],
      preApproved: true
    }
  },
  {
    id: 11,
    name: 'Nicole F.',
    tier: 'silver',
    location: 'Denver, CO',
    creditScore: 598,
    monthlyIncome: 5200,
    swipes: 15,
    onboarding: {
      budget: { min: 275000, max: 400000 },
      bedrooms: 2,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['aircon', 'appliances'],
      preApproved: false
    }
  },
  {
    id: 12,
    name: 'Ryan C.',
    tier: 'gold',
    location: 'Golden, CO',
    creditScore: 725,
    monthlyIncome: 6400,
    swipes: 44,
    onboarding: {
      budget: { min: 375000, max: 525000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '1-3 months',
      amenities: ['view', 'garage', 'fireplace'],
      preApproved: true
    }
  },
  {
    id: 13,
    name: 'Lauren B.',
    tier: 'platinum',
    location: 'Denver, CO',
    creditScore: 780,
    monthlyIncome: 9100,
    swipes: 55,
    onboarding: {
      budget: { min: 550000, max: 750000 },
      bedrooms: 4,
      bathrooms: 3,
      livingSituation: 'ownhome',
      timeline: '1-3 months',
      amenities: ['pool', 'office', 'walkincloset', 'security'],
      preApproved: true
    }
  },
  {
    id: 14,
    name: 'Tyler M.',
    tier: 'bronze',
    location: 'Northglenn, CO',
    creditScore: 608,
    monthlyIncome: 3400,
    swipes: 9,
    onboarding: {
      budget: { min: 175000, max: 275000 },
      bedrooms: 2,
      bathrooms: 1,
      livingSituation: 'family',
      timeline: '6+ months',
      amenities: ['garage'],
      preApproved: false
    }
  },
  {
    id: 15,
    name: 'Jessica K.',
    tier: 'silver',
    location: 'Highlands Ranch, CO',
    creditScore: 685,
    monthlyIncome: 3800,
    swipes: 21,
    onboarding: {
      budget: { min: 300000, max: 425000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['garden', 'aircon', 'appliances'],
      preApproved: false
    }
  },
  {
    id: 16,
    name: 'Brandon H.',
    tier: 'bronze',
    location: 'Denver, CO',
    creditScore: 571,
    monthlyIncome: 2900,
    swipes: 7,
    onboarding: {
      budget: { min: 125000, max: 200000 },
      bedrooms: 1,
      bathrooms: 1,
      livingSituation: 'family',
      timeline: '6+ months',
      amenities: ['aircon'],
      preApproved: false
    }
  },
  {
    id: 17,
    name: 'Megan D.',
    tier: 'platinum',
    location: 'Castle Rock, CO',
    creditScore: 745,
    monthlyIncome: 8900,
    swipes: 61,
    onboarding: {
      budget: { min: 525000, max: 725000 },
      bedrooms: 4,
      bathrooms: 3,
      livingSituation: 'ownhome',
      timeline: '1-3 months',
      amenities: ['pool', 'view', 'garage', 'garden'],
      preApproved: true
    }
  },
  {
    id: 18,
    name: 'Kevin R.',
    tier: 'gold',
    location: 'Denver, CO',
    creditScore: 688,
    monthlyIncome: 5400,
    swipes: 33,
    onboarding: {
      budget: { min: 300000, max: 450000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['hardwood', 'balcony'],
      preApproved: true
    }
  },
  {
    id: 19,
    name: 'Ashley T.',
    tier: 'silver',
    location: 'Wheat Ridge, CO',
    creditScore: 612,
    monthlyIncome: 4800,
    swipes: 18,
    onboarding: {
      budget: { min: 250000, max: 375000 },
      bedrooms: 2,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['aircon', 'garden'],
      preApproved: false
    }
  },
  {
    id: 20,
    name: 'Daniel F.',
    tier: 'gold',
    location: 'Denver, CO',
    creditScore: 731,
    monthlyIncome: 7600,
    swipes: 42,
    onboarding: {
      budget: { min: 425000, max: 600000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '1-3 months',
      amenities: ['office', 'garage', 'hardwood'],
      preApproved: true
    }
  },
  {
    id: 21,
    name: 'Samantha G.',
    tier: 'platinum',
    location: 'Louisville, CO',
    creditScore: 768,
    monthlyIncome: 9200,
    swipes: 57,
    onboarding: {
      budget: { min: 575000, max: 800000 },
      bedrooms: 4,
      bathrooms: 3,
      livingSituation: 'ownhome',
      timeline: '1-3 months',
      amenities: ['view', 'walkincloset', 'pool', 'office'],
      preApproved: true
    }
  },
  {
    id: 22,
    name: 'Andrew J.',
    tier: 'bronze',
    location: 'Sheridan, CO',
    creditScore: 558,
    monthlyIncome: 3100,
    swipes: 8,
    onboarding: {
      budget: { min: 150000, max: 225000 },
      bedrooms: 2,
      bathrooms: 1,
      livingSituation: 'family',
      timeline: '6+ months',
      amenities: ['aircon'],
      preApproved: false
    }
  },
  {
    id: 23,
    name: 'Brittany L.',
    tier: 'gold',
    location: 'Denver, CO',
    creditScore: 704,
    monthlyIncome: 6100,
    swipes: 29,
    onboarding: {
      budget: { min: 350000, max: 500000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['balcony', 'gym', 'security'],
      preApproved: true
    }
  },
  {
    id: 24,
    name: 'Joshua N.',
    tier: 'silver',
    location: 'Greenwood Village, CO',
    creditScore: 722,
    monthlyIncome: 3500,
    swipes: 16,
    onboarding: {
      budget: { min: 275000, max: 400000 },
      bedrooms: 2,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '6+ months',
      amenities: ['appliances', 'balcony'],
      preApproved: false
    }
  },
  {
    id: 25,
    name: 'Stephanie P.',
    tier: 'platinum',
    location: 'Denver, CO',
    creditScore: 792,
    monthlyIncome: 8700,
    swipes: 49,
    onboarding: {
      budget: { min: 500000, max: 700000 },
      bedrooms: 3,
      bathrooms: 3,
      livingSituation: 'renting',
      timeline: '1-3 months',
      amenities: ['hardwood', 'view', 'walkincloset', 'office'],
      preApproved: true
    }
  },
  {
    id: 26,
    name: 'Matthew S.',
    tier: 'gold',
    location: 'Lafayette, CO',
    creditScore: 698,
    monthlyIncome: 5800,
    swipes: 36,
    onboarding: {
      budget: { min: 325000, max: 475000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['garage', 'garden', 'fireplace'],
      preApproved: true
    }
  },
  {
    id: 27,
    name: 'Christina V.',
    tier: 'bronze',
    location: 'Edgewater, CO',
    creditScore: 589,
    monthlyIncome: 2800,
    swipes: 6,
    onboarding: {
      budget: { min: 125000, max: 200000 },
      bedrooms: 1,
      bathrooms: 1,
      livingSituation: 'family',
      timeline: '6+ months',
      amenities: ['aircon'],
      preApproved: false
    }
  },
  {
    id: 28,
    name: 'Robert W.',
    tier: 'gold',
    location: 'Denver, CO',
    creditScore: 711,
    monthlyIncome: 6300,
    swipes: 27,
    onboarding: {
      budget: { min: 375000, max: 525000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '1-3 months',
      amenities: ['hardwood', 'office', 'balcony'],
      preApproved: true
    }
  },
  {
    id: 29,
    name: 'Kayla A.',
    tier: 'silver',
    location: 'Morrison, CO',
    creditScore: 595,
    monthlyIncome: 5600,
    swipes: 14,
    onboarding: {
      budget: { min: 300000, max: 425000 },
      bedrooms: 2,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['view', 'garden'],
      preApproved: false
    }
  },
  {
    id: 30,
    name: 'Brian E.',
    tier: 'platinum',
    location: 'Cherry Hills Village, CO',
    creditScore: 801,
    monthlyIncome: 12400,
    swipes: 71,
    onboarding: {
      budget: { min: 750000, max: 1200000 },
      bedrooms: 5,
      bathrooms: 4,
      livingSituation: 'ownhome',
      timeline: '1-3 months',
      amenities: ['pool', 'view', 'walkincloset', 'office', 'security', 'gym'],
      preApproved: true
    }
  },
  {
    id: 31,
    name: 'Michelle I.',
    tier: 'gold',
    location: 'Denver, CO',
    creditScore: 682,
    monthlyIncome: 5200,
    swipes: 24,
    onboarding: {
      budget: { min: 300000, max: 425000 },
      bedrooms: 2,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['balcony', 'appliances'],
      preApproved: true
    }
  },
  {
    id: 32,
    name: 'Steven O.',
    tier: 'silver',
    location: 'Bennett, CO',
    creditScore: 701,
    monthlyIncome: 3900,
    swipes: 11,
    onboarding: {
      budget: { min: 225000, max: 350000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'family',
      timeline: '6+ months',
      amenities: ['garage', 'garden'],
      preApproved: false
    }
  },
  {
    id: 33,
    name: 'Amber U.',
    tier: 'bronze',
    location: 'Denver, CO',
    creditScore: 567,
    monthlyIncome: 3300,
    swipes: 5,
    onboarding: {
      budget: { min: 150000, max: 225000 },
      bedrooms: 2,
      bathrooms: 1,
      livingSituation: 'family',
      timeline: '6+ months',
      amenities: ['aircon'],
      preApproved: false
    }
  },
  {
    id: 34,
    name: 'Timothy Y.',
    tier: 'platinum',
    location: 'Bow Mar, CO',
    creditScore: 758,
    monthlyIncome: 9800,
    swipes: 58,
    onboarding: {
      budget: { min: 600000, max: 850000 },
      bedrooms: 4,
      bathrooms: 3,
      livingSituation: 'ownhome',
      timeline: '1-3 months',
      amenities: ['pool', 'view', 'garage', 'walkincloset'],
      preApproved: true
    }
  },
  {
    id: 35,
    name: 'Heather C.',
    tier: 'gold',
    location: 'Frederick, CO',
    creditScore: 719,
    monthlyIncome: 6700,
    swipes: 39,
    onboarding: {
      budget: { min: 375000, max: 525000 },
      bedrooms: 4,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '1-3 months',
      amenities: ['garage', 'garden', 'fireplace'],
      preApproved: true
    }
  },
  {
    id: 36,
    name: 'Patrick Q.',
    tier: 'silver',
    location: 'Denver, CO',
    creditScore: 608,
    monthlyIncome: 4600,
    swipes: 17,
    onboarding: {
      budget: { min: 250000, max: 375000 },
      bedrooms: 2,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['aircon', 'balcony'],
      preApproved: false
    }
  },
  {
    id: 37,
    name: 'Tiffany Z.',
    tier: 'gold',
    location: 'Columbine, CO',
    creditScore: 694,
    monthlyIncome: 5500,
    swipes: 32,
    onboarding: {
      budget: { min: 325000, max: 450000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['garden', 'hardwood', 'garage'],
      preApproved: true
    }
  },
  {
    id: 38,
    name: 'Gregory X.',
    tier: 'platinum',
    location: 'Denver, CO',
    creditScore: 773,
    monthlyIncome: 8400,
    swipes: 46,
    onboarding: {
      budget: { min: 475000, max: 675000 },
      bedrooms: 3,
      bathrooms: 3,
      livingSituation: 'renting',
      timeline: '1-3 months',
      amenities: ['view', 'office', 'hardwood', 'balcony'],
      preApproved: true
    }
  },
  {
    id: 39,
    name: 'Danielle J.',
    tier: 'bronze',
    location: 'Dupont, CO',
    creditScore: 551,
    monthlyIncome: 2700,
    swipes: 4,
    onboarding: {
      budget: { min: 100000, max: 175000 },
      bedrooms: 1,
      bathrooms: 1,
      livingSituation: 'family',
      timeline: '6+ months',
      amenities: ['aircon'],
      preApproved: false
    }
  },
  {
    id: 40,
    name: 'Eric N.',
    tier: 'gold',
    location: 'Roxborough Park, CO',
    creditScore: 707,
    monthlyIncome: 6000,
    swipes: 35,
    onboarding: {
      budget: { min: 350000, max: 500000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['view', 'garage', 'fireplace'],
      preApproved: true
    }
  },
  {
    id: 41,
    name: 'Courtney B.',
    tier: 'silver',
    location: 'Glendale, CO',
    creditScore: 715,
    monthlyIncome: 3700,
    swipes: 13,
    onboarding: {
      budget: { min: 250000, max: 375000 },
      bedrooms: 2,
      bathrooms: 1,
      livingSituation: 'renting',
      timeline: '6+ months',
      amenities: ['balcony', 'gym'],
      preApproved: false
    }
  },
  {
    id: 42,
    name: 'Jonathan D.',
    tier: 'platinum',
    location: 'Denver, CO',
    creditScore: 784,
    monthlyIncome: 9500,
    swipes: 64,
    onboarding: {
      budget: { min: 550000, max: 775000 },
      bedrooms: 4,
      bathrooms: 3,
      livingSituation: 'ownhome',
      timeline: '1-3 months',
      amenities: ['pool', 'office', 'walkincloset', 'security', 'garage'],
      preApproved: true
    }
  },
  {
    id: 43,
    name: 'Lindsey F.',
    tier: 'gold',
    location: 'Welby, CO',
    creditScore: 676,
    monthlyIncome: 5100,
    swipes: 22,
    onboarding: {
      budget: { min: 275000, max: 400000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['garage', 'garden'],
      preApproved: true
    }
  },
  {
    id: 44,
    name: 'Mark H.',
    tier: 'bronze',
    location: 'Berkley, CO',
    creditScore: 583,
    monthlyIncome: 3000,
    swipes: 10,
    onboarding: {
      budget: { min: 150000, max: 225000 },
      bedrooms: 2,
      bathrooms: 1,
      livingSituation: 'family',
      timeline: '6+ months',
      amenities: ['aircon', 'garage'],
      preApproved: false
    }
  },
  {
    id: 45,
    name: 'Vanessa L.',
    tier: 'gold',
    location: 'Denver, CO',
    creditScore: 728,
    monthlyIncome: 6900,
    swipes: 40,
    onboarding: {
      budget: { min: 400000, max: 575000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '1-3 months',
      amenities: ['hardwood', 'balcony', 'office'],
      preApproved: true
    }
  },
  {
    id: 46,
    name: 'Derek P.',
    tier: 'silver',
    location: 'Todd Creek, CO',
    creditScore: 591,
    monthlyIncome: 5300,
    swipes: 20,
    onboarding: {
      budget: { min: 275000, max: 400000 },
      bedrooms: 3,
      bathrooms: 2,
      livingSituation: 'renting',
      timeline: '3-6 months',
      amenities: ['garage', 'garden'],
      preApproved: false
    }
  }
];

const DenverTrial = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  
  // Email gate state
  const [hasAccess, setHasAccess] = useState(false);
  const [gateEmail, setGateEmail] = useState('');
  const [gateError, setGateError] = useState('');
  const [gateLoading, setGateLoading] = useState(false);

  const handleGateSubmit = async (e) => {
    e.preventDefault();
    setGateError('');
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!gateEmail || !emailRegex.test(gateEmail)) {
      setGateError('Please enter a valid email address');
      return;
    }

    // Block common personal email domains
    const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'mail.com', 'protonmail.com'];
    const emailDomain = gateEmail.split('@')[1]?.toLowerCase();
    if (personalDomains.includes(emailDomain)) {
      setGateError('Please use your real estate business email');
      return;
    }

    setGateLoading(true);

    try {
      // Send notification email via backend
      const response = await fetch('https://back-end-x7tx.onrender.com/api/notify-lead-visitor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: gateEmail,
          page: 'denver-trial',
          city: CITY_CONFIG.cityName,
          timestamp: new Date().toISOString()
        }),
      });

      // Grant access regardless of email send success (don't block user)
      setHasAccess(true);
      
      // Store in localStorage so they don't have to re-enter
      localStorage.setItem('lauft_agent_access', JSON.stringify({
        email: gateEmail,
        timestamp: Date.now()
      }));

      // Fire Meta Pixel event
      if (typeof window.fbq !== 'undefined') {
        window.fbq('track', 'Lead', {
          content_name: 'Agent Preview Gate',
          content_category: CITY_CONFIG.cityName
        });
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      // Still grant access even if notification fails
      setHasAccess(true);
      localStorage.setItem('lauft_agent_access', JSON.stringify({
        email: gateEmail,
        timestamp: Date.now()
      }));
    } finally {
      setGateLoading(false);
    }
  };

  // Check for existing access on mount
  useEffect(() => {
    const storedAccess = localStorage.getItem('lauft_agent_access');
    if (storedAccess) {
      try {
        const { timestamp } = JSON.parse(storedAccess);
        // Access expires after 7 days
        if (Date.now() - timestamp < 7 * 24 * 60 * 60 * 1000) {
          setHasAccess(true);
        } else {
          localStorage.removeItem('lauft_agent_access');
        }
      } catch (e) {
        localStorage.removeItem('lauft_agent_access');
      }
    }
  }, []);

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  const handleClaimFromDetails = () => {
    setShowLeadModal(false);
    setShowClaimModal(true);
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fire Meta Pixel PageView for this preview page
  useEffect(() => {
    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', 'ViewContent', {
        content_name: `${CITY_CONFIG.cityName} Preview Page`,
        content_category: 'Agent Lead Preview'
      });
    }
  }, []);

  const handleStartTrial = () => {
    // Fire Meta Pixel event for trial start click
    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', 'StartTrial', {
        content_name: 'Trial Start Click',
        content_category: CITY_CONFIG.cityName
      });
    }
    window.location.href = CITY_CONFIG.trialUrl;
  };

  const getTierEmoji = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'platinum': return '💎';
      case 'gold': return '🥇';
      case 'silver': return '🥈';
      case 'bronze': return '🥉';
      default: return '❓';
    }
  };

  const getTierColor = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'platinum': return '#3b82f6';
      case 'gold': return '#d97706';
      case 'silver': return '#64748b';
      case 'bronze': return '#ea580c';
      default: return '#6b7280';
    }
  };

  const formatCurrency = (amount) => {
    return `$${amount.toLocaleString()}`;
  };

  const formatAmenity = (amenityId) => {
    const mapping = {
      'hardwood': { emoji: '🪵', label: 'Hardwood Floors' },
      'aircon': { emoji: '❄️', label: 'Central Air/AC' },
      'garage': { emoji: '🚗', label: 'Garage' },
      'appliances': { emoji: '✨', label: 'Stainless Steel' },
      'view': { emoji: '🏔️', label: 'Scenic Views' },
      'pool': { emoji: '🏊', label: 'Swimming Pool' },
      'fireplace': { emoji: '🔥', label: 'Fireplace' },
      'balcony': { emoji: '🌅', label: 'Balcony/Patio' },
      'garden': { emoji: '🌸', label: 'Garden/Yard' },
      'walkincloset': { emoji: '👗', label: 'Walk-in Closet' },
      'security': { emoji: '🔒', label: 'Security System' },
      'gym': { emoji: '💪', label: 'Home Gym' },
      'office': { emoji: '💻', label: 'Home Office' }
    };
    return mapping[amenityId] || { emoji: '✓', label: amenityId };
  };

  const formatLivingSituation = (situation) => {
    const mapping = {
      'renting': 'Currently Renting',
      'ownhome': 'Current Homeowner',
      'family': 'Living with Family'
    };
    return mapping[situation] || situation;
  };

  return (
    <div style={styles.pageContainer}>
      {/* Email Gate */}
      {!hasAccess ? (
        <div style={styles.gateContainer}>
          {/* Background glow */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.03) 0%, transparent 70%)',
            pointerEvents: 'none'
          }}></div>
          
          <div style={styles.gateCard}>
            <img src="/lauft-logo.png" alt="Lauft" style={styles.gateLogo} />
            
            <div style={styles.gateIconCircle}>
              <Lock size={28} color="rgba(255, 255, 255, 0.8)" />
            </div>
            
            <h1 style={styles.gateTitle}>Agents Only</h1>
            <p style={styles.gateSubtitle}>
              This page contains live buyer data for the {CITY_CONFIG.cityName} metro area. Enter your <span style={{ color: '#ffffff', fontWeight: '600' }}>real estate business email</span> to view live buyer profiles.
            </p>
            
            <form onSubmit={handleGateSubmit} style={styles.gateForm}>
              <div style={styles.gateInputWrapper}>
                <input
                  type="email"
                  value={gateEmail}
                  onChange={(e) => setGateEmail(e.target.value)}
                  placeholder="you@yourbrokerage.com"
                  style={styles.gateInput}
                  className="gate-input"
                  disabled={gateLoading}
                />
              </div>
              
              {gateError && (
                <p style={styles.gateError}>{gateError}</p>
              )}
              
              <button 
                type="submit" 
                style={styles.gateButton}
                disabled={gateLoading}
                className="gate-btn"
              >
                {gateLoading ? 'Verifying...' : 'View Live Buyer Data'}
                {!gateLoading && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                )}
              </button>
            </form>
            
            <p style={styles.gateDisclaimer}>
              Personal emails (Gmail, Yahoo, etc.) are not accepted.
            </p>
          </div>
          
          <style>{`
            .gate-btn {
              transition: all 0.2s ease-out;
            }
            
            .gate-btn:hover:not(:disabled) {
              background-color: #ffffff !important;
              color: #1a1a1a !important;
              border-color: #ffffff !important;
              transform: translateY(-3px) scale(1.02);
              box-shadow: 0 10px 30px rgba(255, 255, 255, 0.15);
            }
            
            .gate-btn:hover:not(:disabled) svg {
              transform: translateX(4px);
            }
            
            .gate-btn svg {
              transition: transform 0.2s ease-out;
            }
            
            .gate-btn:active:not(:disabled) {
              transform: translateY(-1px) scale(1);
              background-color: rgba(255, 255, 255, 0.9) !important;
              box-shadow: 0 5px 15px rgba(255, 255, 255, 0.1);
            }
            
            .gate-btn:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
            
            .gate-input {
              transition: all 0.2s ease-out;
            }
            
            .gate-input:hover {
              border-color: rgba(255, 255, 255, 0.25) !important;
            }
            
            .gate-input:focus {
              border-color: rgba(255, 255, 255, 0.4) !important;
              background-color: rgba(255, 255, 255, 0.06) !important;
              outline: none;
            }
            
            .gate-input::placeholder {
              color: rgba(255, 255, 255, 0.3);
            }
          `}</style>
        </div>
      ) : (
        <>
          {/* Header */}
          <header style={styles.header}>
            <img src="/lauft-logo.png" alt="Lauft" style={styles.logo} />
          </header>

          {/* Hero Section - Modern Dark Style */}
          <section style={styles.hero}>
            <div style={styles.heroContent}>
              <div style={styles.heroLabel} className="fade-in-up">AGENT MEMBERSHIP</div>
              
              <h1 style={styles.heroTitle} className="fade-in-up-delay-1">
                Active buyers are waiting.<br />
                <span style={styles.heroHighlight}>Claim them now.</span>
              </h1>
              
              <p style={styles.heroSubtitle} className="fade-in-up-delay-2">
                Start your free 14-day trial to unlock full buyer details and claim leads.
              </p>

          <div style={styles.urgencyBadge} className="fade-in-up-delay-3">
            <span style={styles.urgencyDot}></span>
            Enrollment closes December 20th
          </div>
        </div>
      </section>

      {/* Preview Table Section */}
      <section style={styles.tableSection}>
        <div style={styles.tableCard} className="fade-in-up-delay-4">
          <div style={styles.tableSectionHeader}>
            <h2 style={styles.tableSectionTitle}>
              Available Leads in
              <span style={styles.cityBadge}>{CITY_CONFIG.cityName}</span>
            </h2>
            <div style={styles.liveIndicator}>
              <span style={styles.liveDot}></span>
              Preview Mode
            </div>
          </div>
          
          <p style={styles.tableSubheader}>Click a user to view more details</p>

          {/* Scroll hint for mobile */}
          {isMobile && showScrollHint && (
            <div 
              style={styles.scrollHint}
              onClick={() => setShowScrollHint(false)}
            >
              <span>←</span>
              <span>Scroll to see more</span>
              <span>→</span>
            </div>
          )}

          {/* Table Container - Scrollable */}
          <div 
            style={styles.tableContainer}
          >
            <div 
              style={styles.tableScrollInner} 
              className="table-scroll"
              onScroll={(e) => {
                if (e.target.scrollTop > 10 && !hasScrolled) {
                  setHasScrolled(true);
                }
                if (e.target.scrollLeft > 10) {
                  setShowScrollHint(false);
                }
              }}
            >
              <table style={styles.table} className="preview-table">
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Tier</th>
                    <th style={styles.th}>Location</th>
                    <th style={styles.th}>Credit Score</th>
                    <th style={styles.th}>Monthly Income</th>
                    <th style={styles.th}>Swipes Made</th>
                    <th style={{...styles.th, textAlign: 'center'}}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {FAKE_LEADS.map((lead) => {
                    return (
                      <tr 
                        key={lead.id} 
                        style={{...styles.tr, cursor: 'pointer'}}
                        onClick={() => handleLeadClick(lead)}
                      >
                        {/* Name */}
                        <td style={styles.td}>
                          <div style={styles.nameCell}>
                            {lead.name}
                          </div>
                        </td>

                        {/* Tier */}
                        <td style={styles.td}>
                          <div style={styles.tierCell}>
                            <span style={{ fontSize: '1.25rem' }}>
                              {getTierEmoji(lead.tier)}
                            </span>
                            <span style={{
                              color: getTierColor(lead.tier),
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              fontSize: '0.8rem'
                            }}>
                              {lead.tier}
                            </span>
                          </div>
                        </td>

                        {/* Location */}
                        <td style={styles.td}>
                          <div style={styles.locationCell}>
                            {lead.location}
                          </div>
                        </td>

                        {/* Credit Score */}
                        <td style={styles.td}>
                          <div style={styles.statCell}>
                            {lead.creditScore}
                          </div>
                        </td>

                        {/* Monthly Income */}
                        <td style={styles.td}>
                          <div style={styles.statCell}>
                            {formatCurrency(lead.monthlyIncome)}
                          </div>
                        </td>

                        {/* Swipes Made */}
                        <td style={styles.td}>
                          <div style={styles.swipesBadge}>
                            {lead.swipes}
                          </div>
                        </td>

                        {/* Locked Action */}
                        <td style={{...styles.td, textAlign: 'center'}}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowClaimModal(true);
                            }}
                            style={styles.lockedButton}
                            className="locked-btn"
                          >
                            <Lock size={14} />
                            Claim
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Floating Lock Overlay - Slides up when user scrolls */}
            <div style={{
              ...styles.tableOverlay,
              transform: hasScrolled ? 'translateY(0)' : 'translateY(100%)',
              opacity: hasScrolled ? 1 : 0,
              transition: 'transform 0.4s ease-out, opacity 0.4s ease-out'
            }}>
              <div style={styles.overlayContent}>
                <div style={styles.overlayIconCircle}>
                  <Lock size={22} color="rgba(255, 255, 255, 0.8)" />
                </div>
                <h3 style={styles.overlayTitle}>Ready to connect with these leads and more?</h3>
                <p style={styles.overlayText}>Start your free trial to claim buyers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA - Centered below table */}
        <div style={styles.tableSectionCta}>
          <button onClick={handleStartTrial} style={styles.tableSectionCtaButton} className="primary-cta">
            Start free trial
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
          </button>
          <p style={styles.tableSectionCtaHelper}>14-day free trial. No credit card required.</p>
        </div>
      </section>

      {/* Bottom Stats */}
      <section style={styles.statsSection}>
        <div style={styles.statCard} className="stat-card stat-card-1">
          <div style={styles.statNumber}>500+</div>
          <div style={styles.statLabel}>Active Buyers in {CITY_CONFIG.cityName}</div>
        </div>
        <div style={styles.statCard} className="stat-card stat-card-2">
          <div style={styles.statNumber}>$49</div>
          <div style={styles.statLabel}>Per Month, Cancel Anytime</div>
        </div>
        <div style={styles.statCard} className="stat-card stat-card-3">
          <div style={styles.statNumber}>1:1</div>
          <div style={styles.statLabel}>Exclusive Lead Claiming</div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerTop}>
            <div style={styles.footerCopyright}>
              Copyright 2025, <span style={styles.footerBold}>LAUFT TECH INC</span>. All Rights Reserved.
            </div>
            <div style={styles.footerLinks}>
              <a href="https://lauft.app/terms" target="_blank" rel="noopener noreferrer" style={styles.footerLink} className="footer-link">Terms of Services</a>
              <a href="https://lauft.app/privacy" target="_blank" rel="noopener noreferrer" style={styles.footerLink} className="footer-link">Privacy Policy</a>
            </div>
          </div>
          
          <div style={styles.footerDisclaimer}>
            <p style={styles.disclaimerText}>
              Lauft is a technology platform and does not operate as a licensed real estate brokerage, lender, or financial advisor. We are not affiliated with any financial institution or MLS organization, and we do not guarantee specific outcomes, matches, showings, or transactions. All features, tools, and services available through the Lauft app or website may be updated or discontinued at any time without notice.
            </p>
            <p style={styles.disclaimerText}>
              Agents, lenders, and vendors using Lauft are solely responsible for complying with all applicable real estate, advertising, licensing, and contact laws within their jurisdiction. Lauft does not provide legal, tax, financial, or real estate advice. Any information, materials, or conversations provided are for general informational purposes only and should not be interpreted as professional guidance. Users and professionals should consult with the appropriate licensed expert for advice tailored to their specific situation.
            </p>
            <p style={styles.disclaimerText}>
              Results may vary based on market conditions, user behavior, campaign performance, and individual agent follow-up.
              <br />
              © 2025 Lauft Tech Inc. All rights reserved. Use of Lauft is subject to our <a href="https://lauft.app/terms" target="_blank" rel="noopener noreferrer" style={styles.disclaimerLink} className="footer-link">Terms of Service</a> and <a href="https://lauft.app/privacy" target="_blank" rel="noopener noreferrer" style={styles.disclaimerLink} className="footer-link">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </footer>

      {/* Lead Details Modal - Light Theme */}
      {showLeadModal && selectedLead && (
        <div style={styles.modalOverlay} onClick={() => setShowLeadModal(false)}>
          <div style={styles.leadModal} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={styles.leadModalHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div>
                  <h2 style={styles.leadModalTitle}>{selectedLead.name}</h2>
                  <p style={styles.leadModalSubtitle}>User Preview</p>
                </div>
                <div style={styles.leadModalTierBadge}>
                  <span style={{ fontSize: '1.25rem' }}>{getTierEmoji(selectedLead.tier)}</span>
                  <span style={{ 
                    color: getTierColor(selectedLead.tier), 
                    fontWeight: '600', 
                    textTransform: 'uppercase', 
                    fontSize: '0.85rem' 
                  }}>
                    {selectedLead.tier}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {!isMobile && (
                  <button 
                    onClick={handleClaimFromDetails}
                    style={styles.leadModalHeaderButton}
                    className="lead-header-btn"
                  >
                    Claim User
                  </button>
                )}
                <button 
                  style={styles.leadModalCloseButton} 
                  onClick={() => setShowLeadModal(false)}
                  className="lead-close-btn"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={styles.leadModalContent}>
              {/* Unlock Banner */}
              <div style={styles.leadModalUnlockBanner}>
                <Lock size={16} style={{ marginRight: '8px', flexShrink: 0 }} />
                Claim this user to unlock full contact information and connect directly on or off platform
              </div>

              {/* Contact Info Row */}
              <div style={styles.leadModalInfoRow}>
                <div style={styles.leadModalInfoItem}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(26, 26, 26, 0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <div>
                    <p style={styles.leadModalInfoLabel}>Phone</p>
                    <p style={styles.leadModalInfoValue}>***-***-****</p>
                  </div>
                </div>
                <div style={styles.leadModalInfoItem}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(26, 26, 26, 0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <div>
                    <p style={styles.leadModalInfoLabel}>Email</p>
                    <p style={styles.leadModalInfoValue}>****@****.***</p>
                  </div>
                </div>
                <div style={styles.leadModalInfoItem}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(26, 26, 26, 0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <div>
                    <p style={styles.leadModalInfoLabel}>Location</p>
                    <p style={styles.leadModalInfoValue}>{selectedLead.location.replace(', CO', ', Colorado')}</p>
                  </div>
                </div>
              </div>

              {/* Stats Box */}
              <div style={styles.leadModalStatsBox}>
                <div style={styles.leadModalStatItem}>
                  <div style={styles.leadModalStatNumber}>{selectedLead.swipes}</div>
                  <div style={styles.leadModalStatLabel}>LIKED</div>
                </div>
                <div style={styles.leadModalStatDivider}></div>
                <div style={styles.leadModalStatItem}>
                  <div style={styles.leadModalStatNumber}>{Math.floor(selectedLead.swipes * 0.3)}</div>
                  <div style={styles.leadModalStatLabel}>PASSED</div>
                </div>
                <div style={styles.leadModalStatDivider}></div>
                <div style={styles.leadModalStatItem}>
                  <div style={styles.leadModalStatNumber}>{selectedLead.swipes + Math.floor(selectedLead.swipes * 0.3)}</div>
                  <div style={styles.leadModalStatLabel}>TOTAL</div>
                </div>
              </div>

              {/* Details List */}
              {selectedLead.onboarding && (
                <div style={styles.leadModalDetailsList}>
                  <div style={styles.leadModalDetailRow}>
                    <span style={styles.leadModalDetailLabel}>Living Situation</span>
                    <span style={styles.leadModalDetailValue}>{formatLivingSituation(selectedLead.onboarding.livingSituation)}</span>
                  </div>
                  <div style={styles.leadModalDetailRow}>
                    <span style={styles.leadModalDetailLabel}>Goal Budget</span>
                    <span style={styles.leadModalDetailValue}>
                      {formatCurrency(selectedLead.onboarding.budget?.min)} - {formatCurrency(selectedLead.onboarding.budget?.max)}
                    </span>
                  </div>
                  <div style={styles.leadModalDetailRow}>
                    <span style={styles.leadModalDetailLabel}>Monthly Income</span>
                    <span style={styles.leadModalDetailValue}>{formatCurrency(selectedLead.monthlyIncome)}</span>
                  </div>
                  <div style={styles.leadModalDetailRow}>
                    <span style={styles.leadModalDetailLabel}>Credit Score</span>
                    <span style={styles.leadModalDetailValue}>{selectedLead.creditScore}</span>
                  </div>
                  <div style={styles.leadModalDetailRow}>
                    <span style={styles.leadModalDetailLabel}>Bedrooms</span>
                    <span style={styles.leadModalDetailValue}>{selectedLead.onboarding.bedrooms}+</span>
                  </div>
                  <div style={styles.leadModalDetailRow}>
                    <span style={styles.leadModalDetailLabel}>Bathrooms</span>
                    <span style={styles.leadModalDetailValue}>{selectedLead.onboarding.bathrooms}+</span>
                  </div>

                  {/* Amenities */}
                  {selectedLead.onboarding.amenities && selectedLead.onboarding.amenities.length > 0 && (
                    <div style={styles.leadModalAmenitiesRow}>
                      <span style={styles.leadModalDetailLabel}>Desired Amenities</span>
                      <div style={styles.leadModalAmenitiesList}>
                        {selectedLead.onboarding.amenities.map((amenity, index) => {
                          const formatted = formatAmenity(amenity);
                          return (
                            <div key={index} style={styles.leadModalAmenityItem}>
                              <span>{formatted.emoji}</span>
                              <span>{formatted.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Footer CTA */}
              <div style={styles.leadModalFooter}>
                <button 
                  onClick={handleClaimFromDetails}
                  style={styles.leadModalClaimButton}
                  className="lead-claim-btn"
                >
                  Claim User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim Modal */}
      {showClaimModal && (
        <div style={styles.modalOverlay} onClick={() => setShowClaimModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button 
              style={styles.modalClose} 
              onClick={() => setShowClaimModal(false)}
              className="modal-close"
            >
              <X size={20} />
            </button>
            <div style={styles.modalIconCircle}>
              <Lock size={24} color="rgba(255, 255, 255, 0.8)" />
            </div>
            <h3 style={styles.modalTitle}>Want to claim this lead?</h3>
            <p style={styles.modalText}>
              Start your free 14-day trial to create your agent account and claim buyers in {CITY_CONFIG.cityName}.
            </p>
            <button onClick={handleStartTrial} style={styles.modalButton} className="modal-btn">
              Start free trial
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Sticky CTA */}
      {isMobile && (
        <div style={styles.stickyMobileCta}>
          <div style={styles.stickyPrice}>
            <span style={styles.stickyPriceAmount}>$49</span>
            <span style={styles.stickyPriceLabel}>/month</span>
          </div>
          <button onClick={handleStartTrial} style={styles.stickyButton} className="sticky-cta">
            Start Trial
          </button>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        
        .fade-in-up-delay-1 {
          opacity: 0;
          animation: fadeInUp 0.6s ease-out 0.1s forwards;
        }
        
        .fade-in-up-delay-2 {
          opacity: 0;
          animation: fadeInUp 0.6s ease-out 0.2s forwards;
        }
        
        .fade-in-up-delay-3 {
          opacity: 0;
          animation: fadeInUp 0.6s ease-out 0.3s forwards;
        }
        
        .fade-in-up-delay-4 {
          opacity: 0;
          animation: fadeInUp 0.6s ease-out 0.4s forwards;
        }
        
        .fade-in {
          animation: fadeIn 0.5s ease-out forwards;
        }
        
        * {
          box-sizing: border-box;
        }
        
        body {
          margin: 0;
          padding: 0;
        }
        
        .stat-card-1 {
          opacity: 0;
          animation: fadeInUp 0.5s ease-out 0.1s forwards;
        }
        
        .stat-card-2 {
          opacity: 0;
          animation: fadeInUp 0.5s ease-out 0.2s forwards;
        }
        
        .stat-card-3 {
          opacity: 0;
          animation: fadeInUp 0.5s ease-out 0.3s forwards;
        }
        
        /* Custom scrollbar for table */
        .table-scroll::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        
        .table-scroll::-webkit-scrollbar-track {
          background: rgba(26, 26, 26, 0.05);
          border-radius: 3px;
        }
        
        .table-scroll::-webkit-scrollbar-thumb {
          background: rgba(26, 26, 26, 0.2);
          border-radius: 3px;
        }
        
        .table-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(26, 26, 26, 0.3);
        }
        
        /* Table row hover effect */
        .preview-table tbody tr:hover {
          background-color: rgba(253, 215, 0, 0.05) !important;
        }
        
        /* Locked button hover */
        .locked-btn:hover {
          background-color: rgba(253, 215, 0, 0.1) !important;
          border-color: #fdd700 !important;
        }
        
        /* Primary CTA hover */
        .primary-cta:hover {
          background-color: #f0f0f0 !important;
          transform: translateY(-1px);
        }
        
        /* Mobile sticky button hover */
        .sticky-cta:hover {
          background-color: #f0f0f0 !important;
        }
        
        /* Footer link hover */
        .footer-link:hover {
          opacity: 0.7;
        }
        
        /* Stat card hover */
        .stat-card:hover {
          background-color: rgba(255, 255, 255, 0.08) !important;
          border-color: rgba(255, 255, 255, 0.5) !important;
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }
        
        /* Modal close hover */
        .modal-close:hover {
          color: #ffffff !important;
        }
        
        /* Modal button hover */
        .modal-btn:hover {
          background-color: #f0f0f0 !important;
          transform: translateY(-1px);
        }
        
        /* Lead modal claim button hover */
        .lead-claim-btn:hover {
          background-color: rgba(26, 26, 26, 0.05) !important;
        }
        
        /* Lead modal header button hover */
        .lead-header-btn:hover {
          background-color: rgba(26, 26, 26, 0.05) !important;
        }
        
        /* Lead modal close button hover */
        .lead-close-btn:hover {
          color: #1a1a1a !important;
        }
        
        /* Gate button hover */
        .gate-btn:hover:not(:disabled) {
          background-color: #ffffff !important;
          color: #1a1a1a !important;
          border-color: #ffffff !important;
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 10px 30px rgba(255, 255, 255, 0.15);
        }
        
        .gate-btn:hover:not(:disabled) svg {
          transform: translateX(4px);
        }
        
        .gate-btn svg {
          transition: transform 0.2s ease-out;
        }
        
        .gate-btn:active:not(:disabled) {
          transform: translateY(-1px) scale(1);
          background-color: rgba(255, 255, 255, 0.9) !important;
          box-shadow: 0 5px 15px rgba(255, 255, 255, 0.1);
        }
        
        .gate-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .gate-input:hover {
          border-color: rgba(255, 255, 255, 0.25) !important;
        }
        
        .gate-input:focus {
          border-color: rgba(255, 255, 255, 0.4) !important;
          background-color: rgba(255, 255, 255, 0.06) !important;
        }
        
        .gate-input::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }
      `}</style>
        </>
      )}
    </div>
  );
};

// ============ STYLES - LIGHT MODE ============
// Color Palette: #fcfcfc, #eeebe3, #fdd700, #1a1a1a
const styles = {
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#1a1a1a',
    color: '#ffffff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },

  // Email Gate Styles - Dark Modern Theme
  gateContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: '#1a1a1a',
    position: 'relative',
    overflow: 'hidden'
  },
  gateCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '24px',
    padding: '52px 44px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    backdropFilter: 'blur(20px)',
    position: 'relative',
    zIndex: 1
  },
  gateLogo: {
    height: '28px',
    marginBottom: '44px',
    opacity: 0.9
  },
  gateIconCircle: {
    width: '80px',
    height: '80px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 32px'
  },
  gateTitle: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#ffffff',
    margin: '0 0 14px',
    letterSpacing: '-0.5px'
  },
  gateSubtitle: {
    fontSize: '15px',
    color: 'rgba(255, 255, 255, 0.5)',
    lineHeight: '1.7',
    margin: '0 0 40px'
  },
  gateForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  gateInputWrapper: {
    position: 'relative'
  },
  gateInput: {
    width: '100%',
    padding: '14px 18px',
    fontSize: '15px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxSizing: 'border-box',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    color: '#ffffff'
  },
  gateError: {
    color: '#f87171',
    fontSize: '13px',
    margin: '0',
    textAlign: 'left',
    paddingLeft: '4px'
  },
  gateButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '100%',
    padding: '14px 24px',
    fontSize: '15px',
    fontWeight: '600',
    backgroundColor: 'transparent',
    color: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    marginTop: '8px'
  },
  gateDisclaimer: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.3)',
    lineHeight: '1.7',
    margin: '32px 0 0'
  },

  // Header - Dark
  header: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    position: 'sticky',
    top: 0,
    backgroundColor: 'rgba(26, 26, 26, 0.95)',
    backdropFilter: 'blur(10px)',
    zIndex: 100
  },
  logo: {
    height: '32px',
    width: 'auto'
  },

  // Hero - Modern Dark Style
  hero: {
    padding: '60px 24px 40px',
    backgroundColor: '#1a1a1a',
    color: '#ffffff'
  },
  heroContent: {
    maxWidth: '600px',
    margin: '0 auto'
  },
  heroLabel: {
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '1.5px',
    color: 'rgba(255, 255, 255, 0.6)',
    marginBottom: '24px',
    textTransform: 'uppercase'
  },
  heroTitle: {
    fontSize: 'clamp(36px, 6vw, 52px)',
    fontWeight: '400',
    lineHeight: '1.1',
    margin: '0 0 24px 0',
    color: '#ffffff',
    fontFamily: 'Georgia, "Times New Roman", serif'
  },
  heroHighlight: {
    fontWeight: '700'
  },
  heroSubtitle: {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: '1.6',
    margin: '0'
  },
  boldText: {
    fontWeight: '700',
    color: '#ffffff'
  },
  urgencyBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '24px',
    padding: '10px 18px',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '24px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.9)'
  },
  urgencyDot: {
    width: '8px',
    height: '8px',
    backgroundColor: '#ffffff',
    borderRadius: '50%',
    animation: 'pulse 2s infinite'
  },

  // Table Section - Dark background with light card
  tableSection: {
    padding: '24px 24px 24px',
    position: 'relative',
    backgroundColor: '#1a1a1a'
  },
  tableCard: {
    backgroundColor: '#f9f8f5',
    borderRadius: '16px',
    border: '1px solid rgba(26, 26, 26, 0.08)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
    maxWidth: '1000px',
    margin: '0 auto'
  },
  tableSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    backgroundColor: '#1a1a1a',
    borderBottom: 'none',
    flexWrap: 'wrap',
    gap: '12px'
  },
  tableSectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '20px',
    fontWeight: '600',
    margin: 0,
    color: '#ffffff'
  },
  cityBadge: {
    display: 'inline-block',
    backgroundColor: '#ffffff',
    color: '#1a1a1a',
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '15px',
    fontWeight: '600'
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.6)',
    fontWeight: '500'
  },
  liveDot: {
    width: '8px',
    height: '8px',
    backgroundColor: '#16a34a',
    borderRadius: '50%',
    animation: 'pulse 2s infinite'
  },
  tableSubheader: {
    margin: 0,
    padding: '0 24px 16px',
    fontSize: '13px',
    color: 'rgba(26, 26, 26, 0.5)',
    fontWeight: '400'
  },
  tableContainer: {
    position: 'relative',
    height: '540px',
    overflow: 'hidden'
  },
  scrollHint: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px',
    backgroundColor: 'rgba(253, 215, 0, 0.1)',
    fontSize: '13px',
    color: '#1a1a1a',
    cursor: 'pointer',
    fontWeight: '500',
    borderBottom: '1px solid rgba(26, 26, 26, 0.08)'
  },
  tableScrollInner: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '800px'
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'rgba(26, 26, 26, 0.7)',
    borderBottom: '2px solid rgba(26, 26, 26, 0.1)',
    backgroundColor: 'rgba(253, 215, 0, 0.08)',
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid rgba(26, 26, 26, 0.08)',
    transition: 'background-color 0.2s'
  },
  td: {
    padding: '16px',
    verticalAlign: 'middle',
    color: '#1a1a1a'
  },
  nameCell: {
    color: '#1a1a1a',
    fontSize: '0.9rem',
    fontWeight: '600'
  },
  tierCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  locationCell: {
    color: '#1a1a1a',
    fontSize: '0.9rem'
  },
  statCell: {
    color: '#1a1a1a',
    fontSize: '0.9rem',
    fontWeight: '600'
  },
  swipesBadge: {
    display: 'inline-block',
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    border: '1px solid rgba(99, 102, 241, 0.25)',
    color: '#6366f1',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: '600'
  },
  lockedButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(26, 26, 26, 0.2)',
    color: '#1a1a1a',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },

  // Table Overlay - Dark modern design
  tableOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(180deg, transparent 0%, rgba(26, 26, 26, 0.7) 40%, rgba(26, 26, 26, 0.95) 100%)',
    pointerEvents: 'none'
  },
  overlayContent: {
    textAlign: 'center',
    padding: '60px 20px 28px',
    pointerEvents: 'auto'
  },
  overlayIconCircle: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: 'rgba(26, 26, 26, 0.8)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px'
  },
  overlayTitle: {
    fontSize: '20px',
    fontWeight: '600',
    margin: '0 0 6px',
    color: '#ffffff'
  },
  overlayText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '14px',
    margin: '0'
  },

  // Table Section Bottom CTA
  tableSectionCta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '40px'
  },
  tableSectionCtaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#ffffff',
    color: '#1a1a1a',
    border: 'none',
    padding: '16px 40px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    justifyContent: 'center'
  },
  tableSectionCtaHelper: {
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.5)',
    marginTop: '12px',
    marginBottom: '0'
  },

  // Stats Section - Dark background, tight spacing
  statsSection: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    padding: '0 24px 60px',
    backgroundColor: '#1a1a1a',
    flexWrap: 'wrap'
  },
  statCard: {
    textAlign: 'center',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '12px',
    padding: '24px 32px',
    minWidth: '180px',
    cursor: 'default',
    transition: 'all 0.3s ease'
  },
  statNumber: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#ffffff',
    transition: 'all 0.3s ease'
  },
  statLabel: {
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.5)',
    marginTop: '4px',
    fontWeight: '500',
    transition: 'all 0.3s ease'
  },

  // Footer
  footer: {
    backgroundColor: '#1a1a1a',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    padding: '40px 24px 100px'
  },
  footerContent: {
    maxWidth: '1000px',
    margin: '0 auto'
  },
  footerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  footerCopyright: {
    fontSize: '15px',
    color: '#ffffff'
  },
  footerBold: {
    fontWeight: '700',
    letterSpacing: '0.5px'
  },
  footerLinks: {
    display: 'flex',
    gap: '40px'
  },
  footerLink: {
    fontSize: '15px',
    color: '#ffffff',
    textDecoration: 'none',
    transition: 'opacity 0.2s'
  },
  footerDisclaimer: {
    paddingLeft: '16px',
    borderLeft: 'none'
  },
  disclaimerText: {
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.5)',
    lineHeight: '1.7',
    margin: '0 0 16px 0'
  },
  disclaimerLink: {
    color: 'rgba(255, 255, 255, 0.7)',
    textDecoration: 'none'
  },

  // Claim Modal - Dark theme
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
    backdropFilter: 'blur(4px)'
  },
  modal: {
    backgroundColor: '#1a1a1a',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    padding: '40px 32px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
  },
  modalClose: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.4)',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s'
  },
  modalIconCircle: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px'
  },
  modalTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#ffffff',
    margin: '0 0 12px'
  },
  modalText: {
    fontSize: '15px',
    color: 'rgba(255, 255, 255, 0.6)',
    lineHeight: '1.6',
    margin: '0 0 24px'
  },
  modalButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    backgroundColor: '#ffffff',
    color: '#1a1a1a',
    border: 'none',
    padding: '16px 32px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    width: '100%'
  },

  // Lead Details Modal - Light Theme
  leadModal: {
    backgroundColor: '#f9f8f5',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '650px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(26, 26, 26, 0.1)',
    position: 'relative'
  },
  leadModalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '24px 24px 20px',
    borderBottom: '1px solid rgba(26, 26, 26, 0.1)'
  },
  leadModalTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    margin: 0,
    color: '#1a1a1a'
  },
  leadModalSubtitle: {
    fontSize: '0.875rem',
    color: 'rgba(26, 26, 26, 0.5)',
    margin: '4px 0 0 0'
  },
  leadModalHeaderButton: {
    backgroundColor: 'transparent',
    color: '#1a1a1a',
    border: '1px solid rgba(26, 26, 26, 0.2)',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  leadModalCloseButton: {
    background: 'none',
    border: 'none',
    color: 'rgba(26, 26, 26, 0.5)',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  leadModalContent: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1
  },
  leadModalUnlockBanner: {
    backgroundColor: 'rgba(26, 26, 26, 0.04)',
    border: '1px solid rgba(26, 26, 26, 0.1)',
    padding: '14px 18px',
    borderRadius: '8px',
    color: 'rgba(26, 26, 26, 0.7)',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
    fontWeight: '500'
  },
  leadModalTierBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(26, 26, 26, 0.05)',
    border: '1px solid rgba(26, 26, 26, 0.12)',
    padding: '8px 14px',
    borderRadius: '8px'
  },
  leadModalInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  leadModalInfoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  leadModalInfoLabel: {
    color: 'rgba(26, 26, 26, 0.5)',
    fontSize: '0.75rem',
    margin: '0 0 2px 0'
  },
  leadModalInfoValue: {
    color: '#1a1a1a',
    fontSize: '0.9rem',
    margin: 0,
    fontWeight: '500'
  },
  leadModalStatsBox: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    backgroundColor: 'rgba(26, 26, 26, 0.03)',
    border: '1px solid rgba(26, 26, 26, 0.08)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  },
  leadModalStatItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px'
  },
  leadModalStatNumber: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1a1a1a'
  },
  leadModalStatLabel: {
    color: 'rgba(26, 26, 26, 0.5)',
    fontSize: '0.8rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  },
  leadModalStatDivider: {
    width: '1px',
    height: '50px',
    backgroundColor: 'rgba(26, 26, 26, 0.15)'
  },
  leadModalDetailsList: {
    display: 'flex',
    flexDirection: 'column'
  },
  leadModalDetailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 0',
    borderBottom: '1px solid rgba(26, 26, 26, 0.08)'
  },
  leadModalDetailLabel: {
    color: 'rgba(26, 26, 26, 0.7)',
    fontSize: '0.9rem'
  },
  leadModalDetailValue: {
    color: '#1a1a1a',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  leadModalAmenitiesRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '14px 0',
    borderBottom: '1px solid rgba(26, 26, 26, 0.08)'
  },
  leadModalAmenitiesList: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px'
  },
  leadModalAmenityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem',
    color: '#1a1a1a'
  },
  leadModalFooter: {
    padding: '24px 0 0',
    display: 'flex',
    justifyContent: 'center'
  },
  leadModalClaimButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    color: '#1a1a1a',
    border: '1px solid rgba(26, 26, 26, 0.2)',
    padding: '14px 40px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },

  // Mobile Sticky CTA - Dark theme
  stickyMobileCta: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    backgroundColor: 'rgba(26, 26, 26, 0.98)',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    zIndex: 1000
  },
  stickyPrice: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '2px'
  },
  stickyPriceAmount: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#ffffff'
  },
  stickyPriceLabel: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.6)'
  },
  stickyButton: {
    backgroundColor: '#ffffff',
    color: '#1a1a1a',
    border: 'none',
    padding: '14px 24px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default DenverTrial;